import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadingIcon from "@mui/icons-material/Downloading";
import ErrorIcon from "@mui/icons-material/Error";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Typography } from "@mui/material";
import { type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { isHttpUrl } from "@/utils/link";

/**
 * 商業登記グリッド（取得状況画面・取得済商業登記の検索結果）で共用するセル描画。
 * 明細（登記1件）の状態とダウンロードURLをもとに「商業登記」列の値とアイコンを決め、
 * 会社法人等番号列には閉鎖登記簿の注記を添える。
 */

/** 「商業登記」列の値を決めるのに必要な明細の項目 */
export interface CommercialBookDownloadSource {
  /** SUCCEEDED / FAILED / null（取得中） */
  status: "SUCCEEDED" | "FAILED" | "REQUESTING" | "NEEDS_REVIEW" | null;
  downloadUrl: string | null;
}

/** 「商業登記」列: 取得失敗 */
export const COMMERCIAL_BOOK_CELL_FAILED = "FAILED";
/** 「商業登記」列: 取得中 */
export const COMMERCIAL_BOOK_CELL_PROCESSING = "PROCESSING";

/**
 * 明細の状態に応じて「商業登記」列のセルに格納する値を決める。
 * 取得成功ならダウンロードURL（http(s) の絶対URLのみ採用し、それ以外は空欄＝リンクなし）、
 * 取得失敗なら FAILED、それ以外（取得中）は PROCESSING
 * @param value 明細の状態とダウンロードURL
 * @returns セルの値
 */
export const getUriIfSuccess = (
  value: CommercialBookDownloadSource
): string => {
  if (value.status === "SUCCEEDED") {
    const downloadUrl = value.downloadUrl ?? "";
    return isHttpUrl(downloadUrl) ? downloadUrl : "";
  } else if (value.status === "FAILED") {
    return COMMERCIAL_BOOK_CELL_FAILED;
  } else {
    return COMMERCIAL_BOOK_CELL_PROCESSING;
  }
};

/**
 * 「商業登記」列のセルを描画する。
 * undefined / "" : 空欄、FAILED : エラーアイコン、PROCESSING : ダウンロード中アイコン、URL : ダウンロードアイコン
 */
export const renderIndicatorIconButton = ({
  value,
}: GridRenderCellParams<string>): React.ReactNode => {
  if (value === undefined) return "";
  if (value === "") return "";

  if (value === COMMERCIAL_BOOK_CELL_FAILED) {
    return (
      <Tooltip title={"取得に失敗しました"}>
        <ErrorIcon />
      </Tooltip>
    );
  } else if (value === COMMERCIAL_BOOK_CELL_PROCESSING) {
    return (
      <Tooltip title={"取得しています"}>
        <DownloadingIcon />
      </Tooltip>
    );
  } else {
    // href に渡すのは http(s) の絶対URLのみ（javascript: / data: 等はリンクにしない）
    if (!isHttpUrl(value)) return "";
    return (
      <Tooltip title={"ダウンロードできます"}>
        <IconButton href={value}>
          <CloudDownloadOutlinedIcon />
        </IconButton>
      </Tooltip>
    );
  }
};

/**
 * 会社法人等番号のセルを描画する。閉鎖登記簿の場合は注記を添える
 */
export const renderCorporateNumberWithClosedCaption = (
  params: GridRenderCellParams<
    string,
    { corporateNumber: string; closed: boolean }
  >
): React.ReactNode => {
  const { corporateNumber, closed } = params.row;
  return (
    <div>
      {corporateNumber}
      {closed && (
        <Typography variant="caption" display="block">
          (閉鎖登記簿)
        </Typography>
      )}
    </div>
  );
};
